import React from 'react';

import { Baseline, ExperienceType, Reference } from '@ninetailed/experience.js-shared';
import { ExtendedExperienceConfiguration } from 'libs/contentful/queries/ninetailed';
import { ComponentTracker } from 'components/shared/Ninetailed/ComponentTracker';
import { getExperiences, shouldRenderBaseline } from 'utils/serverExperiences';
import { NinetailedExperienceTracker } from './NinetailedExperienceTracker';
import { ModuleProps, ModuleTypeName } from 'utils/modules';

export type ExperienceComponent<P> = React.ComponentType<
  Omit<P, 'id'> & {
    ninetailed?: {
      isPersonalized: boolean;
      audience: { id: string };
    };
  }
>;

export type ExperienceBaseProps<
  P,
  PassThroughProps extends Partial<P>,
  Variant extends Pick<P, Exclude<keyof P, keyof PassThroughProps>> & Reference,
> = Baseline<Pick<P, Exclude<keyof P, keyof PassThroughProps>>> & {
  experiences: ExtendedExperienceConfiguration<Variant>[];
  component: React.ComponentType<P>;
  passthroughProps?: PassThroughProps;
};

/**
 * Renders a server Ninetailed experience for a given component
 * https://www.contentful.com/developers/docs/personalization/edge-and-server-side-rendering/
 * @param experiences - The experiences
 * @param component - The component to render
 * @param passthroughProps - The passthrough props to render
 * @param baseline - The baseline entry
 * @returns The rendered component and the component tracker
 */
interface ExperienceComponentProps<P> {
  Component: React.ComponentType<P>;
  passthroughProps: Partial<P> | undefined;
  variant: ModuleProps[ModuleTypeName] | undefined;
  variantIndex: number;
  isPersonalized: boolean;
  audienceId: string;
  experienceId: string;
  variantId: string;
  experienceName: string;
  experienceSnowflakeName: string;
  variantName: string;
  experienceType?: ExperienceType;
  experience?: ExtendedExperienceConfiguration<Reference>;
}

const ExperienceComponent = <P,>({
  Component,
  passthroughProps,
  variant,
  variantIndex,
  isPersonalized,
  audienceId,
  experienceId,
  variantId,
  experienceName,
  experienceSnowflakeName,
  variantName,
  experience,
  experienceType,
}: ExperienceComponentProps<P>) => (
  <>
    <ComponentTracker experience={experience} variant={variant as unknown as Reference} variantIndex={variantIndex} />
    <Component
      {...passthroughProps}
      {...(variant as P)}
      ninetailed={{
        isPersonalized,
        audience: { id: audienceId },
      }}
    />
    <NinetailedExperienceTracker
      experienceId={experienceId}
      variantId={variantId}
      experienceName={experienceName}
      experienceSnowflakeName={experienceSnowflakeName}
      variantName={variantName}
      variantIndex={variantIndex}
      experienceType={experienceType}
    />
  </>
);

export const ServerNinetailedExperience = <
  P,
  PassThroughProps extends Partial<P> = Partial<P>,
  Variant extends Pick<P, Exclude<keyof P, keyof PassThroughProps>> & Reference = Pick<
    P,
    Exclude<keyof P, keyof PassThroughProps>
  > &
    Reference,
>({
  experiences,
  component: Component,
  passthroughProps,
  ...baseline
}: ExperienceBaseProps<P, PassThroughProps, Variant>) => {
  const profileExperiences = getExperiences();

  // Find the actual experience that contains this baseline entry
  const baselineExperience = experiences.find(experience =>
    experience.components?.some(component => 'id' in component.baseline && component.baseline.id === baseline.id),
  );

  // Helper component for baseline rendering
  const BaselineComponent = () => (
    <ExperienceComponent
      Component={Component}
      passthroughProps={passthroughProps}
      variant={baseline as unknown as ModuleProps[ModuleTypeName]}
      variantIndex={0}
      isPersonalized={false}
      audienceId="baseline"
      variantId={baseline.id || 'baseline'}
      experienceName={baselineExperience?.name || 'Baseline'}
      experienceSnowflakeName={baselineExperience?.snowflakeName || ''}
      variantName="Baseline"
      experience={baselineExperience}
      experienceId={baselineExperience?.id || ''}
    />
  );

  if (!profileExperiences || shouldRenderBaseline()) {
    return <BaselineComponent />;
  }

  const experience = experiences.find(experience => profileExperiences[experience.id] !== undefined);

  if (!experience) {
    return <BaselineComponent />;
  }

  const component = experience.components.find(
    component => 'id' in component.baseline && component.baseline.id === baseline.id,
  );
  if (!component) {
    return <BaselineComponent />;
  }

  const variantIndex = profileExperiences[experience.id];
  const selectableVariants = [baseline, ...component.variants];
  const variant = selectableVariants[variantIndex];

  if (variant == null) {
    return <BaselineComponent />;
  }

  if ('hidden' in variant && variant.hidden) {
    return null;
  }

  const variantId = 'id' in variant ? variant.id : 'baseline';
  const variantName = 'internalName' in variant && typeof variant.internalName === 'string' ? variant.internalName : '';

  return (
    <ExperienceComponent
      Component={Component}
      passthroughProps={passthroughProps}
      variant={variant as unknown as ModuleProps[ModuleTypeName]}
      variantIndex={variantIndex}
      isPersonalized={true}
      audienceId={experience.audience?.id || 'unknown'}
      experienceId={experience.id}
      variantId={variantId}
      experienceName={experience.name || ''}
      experienceSnowflakeName={experience.snowflakeName || ''}
      variantName={variantName}
      experience={experience}
      experienceType={experience.type}
    />
  );
};
